import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { useTheme } from './ThemeContext';
import MobileHeader from './MobileHeader';
import MainTableV2 from './MainTableV2';
import Spinner from './Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHandshake,
  faCheck,
  faTimes,
  faEye,
  faEdit,
  faTrash,
  faPlus,
  faSearch,
  faFilter,
  faDownload,
  faEnvelope,
  faPhone,
  faBuilding,
  faCalendar,
  faDollarSign,
  faInfoCircle,
  faSort,
  faSortUp,
  faSortDown,
} from '@fortawesome/free-solid-svg-icons';

const AffiliateManagement = () => {
  const { currentUser } = useContext(AuthContext);
  const { theme } = useTheme();
  const primaryColor = theme.primary;
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [affiliateReferralCodes, setAffiliateReferralCodes] = useState([]);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddCodeModal, setShowAddCodeModal] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', 'loading', null
  const [submitMessage, setSubmitMessage] = useState('');
  const [newCodeData, setNewCodeData] = useState({
    salespersonName: '',
    salespersonEmail: '',
    campaignName: '',
    campaignType: 'direct_referrals',
    commissionRate: '',
    isActive: true,
    maxUses: '',
    expirationDate: ''
  });
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [newAffiliateData, setNewAffiliateData] = useState({
    userId: '', // Will be selected from existing users or created new
    userOption: 'existing', // 'existing' or 'new'
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    businessType: '',
    taxId: '',
    businessAddress: '',
    businessCity: '',
    businessState: '',
    businessZip: '',
    referralMethod: '',
    expectedReferrals: '',
    experience: '',
    comments: ''
  });

  const [editAffiliateData, setEditAffiliateData] = useState({
    affiliateId: '',
    companyName: '',
    contactFirstName: '',
    contactLastName: '',
    contactEmail: '',
    contactPhone: '',
    businessType: '',
    taxId: '',
    businessAddress: '',
    businessCity: '',
    businessState: '',
    businessZip: '',
    marketingMethod: '',
    expectedMonthlyReferrals: '',
    affiliateExperience: '',
    notes: '',
    defaultCommissionRate: '',
    paymentTerms: '',
    minimumPayout: ''
  });


  useEffect(() => {
    loadAffiliates();
  }, []);

  // Reload data when filters change (with debounce for search)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadAffiliates();
    }, searchQuery ? 500 : 0); // 500ms debounce for search, immediate for status filter

    return () => clearTimeout(timeoutId);
  }, [filterStatus, searchQuery]);

  const loadAffiliates = async () => {
    setLoading(true);
    try {
      console.log('Loading affiliates for user:', currentUser.userId);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dataServer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getAffiliates',
          payload: {
            userId: currentUser.userId,
            filters: {
              status: filterStatus,
              searchTerm: searchQuery
            }
          }
        })
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (response.ok) {
        const responseText = await response.text();
        console.log('Raw response text:', responseText);
        
        if (responseText) {
          try {
            const data = JSON.parse(responseText);
            console.log('Parsed response data:', data);
            
            // Handle both direct array and nested data structure
            let affiliatesData = [];
            if (typeof data === 'string') {
              affiliatesData = JSON.parse(data);
            } else if (Array.isArray(data)) {
              affiliatesData = data;
            } else if (data && data.data) {
              affiliatesData = Array.isArray(data.data) ? data.data : [];
            }
            
            console.log('Final affiliates data:', affiliatesData);
            setAffiliates(affiliatesData);
          } catch (parseError) {
            console.error('Error parsing JSON:', parseError);
            console.error('Response text that failed to parse:', responseText);
            setAffiliates([]);
          }
        } else {
          console.warn('Empty response received');
          setAffiliates([]);
        }
      } else {
        const errorText = await response.text();
        console.error('Failed to load affiliates:', response.status, response.statusText);
        console.error('Error response:', errorText);
        setAffiliates([]);
      }
    } catch (error) {
      console.error('Error loading affiliates:', error);
      setAffiliates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (affiliateId, newStatus) => {
    setSubmitStatus('loading');
    setSubmitMessage('Updating affiliate status...');
    try {
      console.log('Updating affiliate status:', affiliateId, newStatus);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dataServer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'updateAffiliateStatus',
          payload: {
            userId: currentUser.userId,
            affiliateId: affiliateId,
            status: newStatus,
            reason: `Status changed by admin`
          }
        })
      });

      console.log('Update response status:', response.status);

      if (response.ok) {
        const responseText = await response.text();
        console.log('Update response text:', responseText);
        
        // Update local state immediately with the new status
        setAffiliates(prev => prev.map(affiliate => 
          affiliate.AffiliateId === affiliateId 
            ? { ...affiliate, Status: newStatus }
            : affiliate
        ));
        
        // Try to parse the full response for additional updates
        if (responseText) {
          try {
            const data = JSON.parse(responseText);
            console.log('Update parsed data:', data);
            
            let updatedAffiliate = null;
            if (typeof data === 'string') {
              updatedAffiliate = JSON.parse(data);
            } else if (data && data.data) {
              updatedAffiliate = data.data;
            } else {
              updatedAffiliate = data;
            }
            
            console.log('Updated affiliate:', updatedAffiliate);
            
            // Update local state with full server response if available
            if (updatedAffiliate && updatedAffiliate.AffiliateId) {
              setAffiliates(prev => prev.map(affiliate => 
                affiliate.AffiliateId === affiliateId ? updatedAffiliate : affiliate
              ));
            }
          } catch (parseError) {
            console.error('Error parsing update response:', parseError);
            console.error('Response text that failed to parse:', responseText);
            // State was already updated above, so this is not critical
          }
        }
        
        setSubmitStatus('success');
        setSubmitMessage(getSuccessMessage(newStatus));
        setShowApprovalModal(false);
        setPendingAction(null);
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSubmitStatus(null);
          setSubmitMessage('');
        }, 3000);
      } else {
        const errorText = await response.text();
        console.error('Failed to update affiliate status:', response.status, response.statusText);
        console.error('Update error response:', errorText);
        
        setSubmitStatus('error');
        setSubmitMessage('Failed to update affiliate status. Please try again.');
        setShowApprovalModal(false);
        setPendingAction(null);
        
        // Clear error message after 5 seconds
        setTimeout(() => {
          setSubmitStatus(null);
          setSubmitMessage('');
        }, 5000);
      }
    } catch (error) {
      console.error('Error updating affiliate status:', error);
      setSubmitStatus('error');
      setSubmitMessage('Failed to update affiliate status. Please try again.');
      setShowApprovalModal(false);
      setPendingAction(null);
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
        setSubmitMessage('');
      }, 5000);
    }
  };

  const loadAvailableUsers = async () => {
    setLoadingUsers(true);
    try {
      console.log('Loading available users for affiliate assignment');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dataServer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getUsers',
          payload: {
            userId: currentUser.userId
          }
        })
      });

      if (response.ok) {
        const responseText = await response.text();
        console.log('Available users response:', responseText);
        
        if (responseText) {
          try {
            const data = JSON.parse(responseText);
            let usersData = [];
            if (typeof data === 'string') {
              usersData = JSON.parse(data);
            } else if (Array.isArray(data)) {
              usersData = data;
            } else if (data && data.data) {
              usersData = Array.isArray(data.data) ? data.data : [];
            }
            
            // Filter out users who are already affiliates and admins
            const nonAffiliateUsers = usersData.filter(user => 
              !user.isAdmin && !user.isAffiliate && user.email
            );
            
            setAvailableUsers(nonAffiliateUsers);
          } catch (parseError) {
            console.error('Error parsing users data:', parseError);
            setAvailableUsers([]);
          }
        } else {
          setAvailableUsers([]);
        }
      } else {
        console.error('Failed to load users:', response.status);
        setAvailableUsers([]);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setAvailableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAddAffiliate = async (e) => {
    e.preventDefault();
    setSubmitStatus('loading');
    setSubmitMessage('Creating affiliate partner...');
    try {
      console.log('Creating new affiliate partner:', newAffiliateData);
      
      let finalUserId = newAffiliateData.userId;
      const subdomain = window.location.hostname.split('.')[0];
      
      // If creating new user, we need to create user first
      if (newAffiliateData.userOption === 'new') {
        const createUserResponse = await fetch(`${process.env.REACT_APP_API_URL}/dataServer`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'createUser',
            payload: {
              email: newAffiliateData.email,
              firstName: newAffiliateData.firstName,
              lastName: newAffiliateData.lastName,
              phone: newAffiliateData.phone,
              adminUserId: currentUser.userId,
              tenantId: currentUser.tenantId || 'fln',
              isAffiliate: true,
              subdomain: subdomain
            }
          })
        });

        if (createUserResponse.ok) {
          const userResponseText = await createUserResponse.text();
          const userData = JSON.parse(userResponseText);
          finalUserId = userData.userId || userData.UserId;
          console.log('Created new user with ID:', finalUserId);
        } else {
          throw new Error('Failed to create new user account');
        }
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/dataServer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'createAffiliateDirectly',
          payload: {
            ...newAffiliateData,
            userId: currentUser.userId, // Admin user ID (who is creating the affiliate)
            affiliateUserId: finalUserId, // New affiliate user ID (if creating new user)
            tenantId: currentUser.tenantId || 'fln',
            subdomain: subdomain,
            timestamp: new Date().toISOString(),
            source: 'admin_created',
            ipAddress: null,
            userAgent: navigator.userAgent
          }
        })
      });

      console.log('Add affiliate response status:', response.status);

      if (response.ok) {
        const responseText = await response.text();
        console.log('Add affiliate response:', responseText);
        
        // Reset form and close modal
        setNewAffiliateData({
          userId: '',
          userOption: 'existing',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          businessType: '',
          taxId: '',
          businessAddress: '',
          businessCity: '',
          businessState: '',
          businessZip: '',
          referralMethod: '',
          expectedReferrals: '',
          experience: '',
          comments: ''
        });
        setShowAddModal(false);
        setSubmitStatus('success');
        setSubmitMessage('Affiliate partner created successfully!');
        
        // Reload affiliates list
        loadAffiliates();
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSubmitStatus(null);
          setSubmitMessage('');
        }, 3000);
      } else {
        const errorText = await response.text();
        console.error('Failed to add affiliate:', response.status, response.statusText);
        console.error('Add affiliate error response:', errorText);
        setSubmitStatus('error');
        setSubmitMessage('Failed to create affiliate partner. Please try again.');
        
        // Clear error message after 5 seconds
        setTimeout(() => {
          setSubmitStatus(null);
          setSubmitMessage('');
        }, 5000);
      }
    } catch (error) {
      console.error('Error adding affiliate:', error);
      setSubmitStatus('error');
      setSubmitMessage('Network error. Please check your connection and try again.');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
        setSubmitMessage('');
      }, 5000);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAffiliateData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditAffiliateData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditAffiliate = async (e) => {
    e.preventDefault();
    setSubmitStatus('loading');
    setSubmitMessage('Updating affiliate information...');
    try {
      console.log('Updating affiliate:', editAffiliateData.affiliateId);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dataServer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'updateAffiliate',
          payload: {
            userId: currentUser.userId,
            ...editAffiliateData,
            defaultCommissionRate: editAffiliateData.defaultCommissionRate ? parseFloat(editAffiliateData.defaultCommissionRate) / 100 : 0.05,
            minimumPayout: editAffiliateData.minimumPayout ? parseFloat(editAffiliateData.minimumPayout) : null
          }
        })
      });

      console.log('Edit affiliate response status:', response.status);

      if (response.ok) {
        const responseText = await response.text();
        console.log('Edit affiliate response:', responseText);
        
        // Close modal and refresh affiliates list
        setShowEditModal(false);
        setSubmitStatus('success');
        setSubmitMessage('Affiliate information updated successfully!');
        loadAffiliates();
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSubmitStatus(null);
          setSubmitMessage('');
        }, 3000);
      } else {
        const errorText = await response.text();
        console.error('Failed to update affiliate:', response.status, response.statusText);
        console.error('Edit affiliate error response:', errorText);
        setSubmitStatus('error');
        setSubmitMessage('Failed to update affiliate information. Please try again.');
        
        // Clear error message after 5 seconds
        setTimeout(() => {
          setSubmitStatus(null);
          setSubmitMessage('');
        }, 5000);
      }
    } catch (error) {
      console.error('Error updating affiliate:', error);
      setSubmitStatus('error');
      setSubmitMessage('Network error. Please check your connection and try again.');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
        setSubmitMessage('');
      }, 5000);
    }
  };

  const handleCodeInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewCodeData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddReferralCode = async (e) => {
    e.preventDefault();
    setSubmitStatus('loading');
    setSubmitMessage('Creating referral code...');
    try {
      console.log('Adding new referral code for affiliate:', selectedAffiliate.AffiliateId);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dataServer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'addAffiliateCode',
          payload: {
            userId: currentUser.userId,
            affiliateId: selectedAffiliate.AffiliateId,
            ...newCodeData,
            commissionRate: newCodeData.commissionRate ? parseFloat(newCodeData.commissionRate) / 100 : null,
            maxUses: newCodeData.maxUses ? parseInt(newCodeData.maxUses) : null,
            expirationDate: newCodeData.expirationDate || null
          }
        })
      });

      console.log('Add code response status:', response.status);

      if (response.ok) {
        const responseText = await response.text();
        console.log('Add code response:', responseText);
        
        // Reset form and close modal
        setNewCodeData({
          salespersonName: '',
          salespersonEmail: '',
          campaignName: '',
          campaignType: 'direct_referrals',
          commissionRate: '',
          isActive: true,
          maxUses: '',
          expirationDate: ''
        });
        setShowAddCodeModal(false);
        setSubmitStatus('success');
        setSubmitMessage('Referral code created successfully!');
        
        // Reload referral codes
        loadAffiliateReferralCodes(selectedAffiliate.AffiliateId);
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSubmitStatus(null);
          setSubmitMessage('');
        }, 3000);
      } else {
        const errorText = await response.text();
        console.error('Failed to add referral code:', response.status, response.statusText);
        console.error('Add code error response:', errorText);
        setSubmitStatus('error');
        setSubmitMessage('Failed to create referral code. Please try again.');
        
        // Clear error message after 5 seconds
        setTimeout(() => {
          setSubmitStatus(null);
          setSubmitMessage('');
        }, 5000);
      }
    } catch (error) {
      console.error('Error adding referral code:', error);
      setSubmitStatus('error');
      setSubmitMessage('Network error. Please check your connection and try again.');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
        setSubmitMessage('');
      }, 5000);
    }
  };

  const loadAffiliateReferralCodes = async (affiliateId) => {
    try {
      console.log('Loading referral codes for affiliate:', affiliateId);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dataServer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getAffiliateCodes',
          payload: {
            userId: currentUser.userId,
            affiliateId: affiliateId
          }
        })
      });

      if (response.ok) {
        const responseText = await response.text();
        console.log('Referral codes response:', responseText);
        
        if (responseText) {
          try {
            const data = JSON.parse(responseText);
            let referralData = [];
            if (typeof data === 'string') {
              referralData = JSON.parse(data);
            } else if (Array.isArray(data)) {
              referralData = data;
            } else if (data && data.data) {
              referralData = Array.isArray(data.data) ? data.data : [];
            }
            
            setAffiliateReferralCodes(referralData);
          } catch (parseError) {
            console.error('Error parsing referral codes:', parseError);
            setAffiliateReferralCodes([]);
          }
        } else {
          setAffiliateReferralCodes([]);
        }
      } else {
        console.error('Failed to load referral codes:', response.status);
        setAffiliateReferralCodes([]);
      }
    } catch (error) {
      console.error('Error loading referral codes:', error);
      setAffiliateReferralCodes([]);
    }
  };

  const filteredAffiliates = affiliates.filter(affiliate => {
    const matchesStatus = filterStatus === 'ALL' || affiliate.Status === filterStatus;
    const matchesSearch = searchQuery === '' || 
      affiliate.CompanyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      affiliate.ContactFirstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      affiliate.ContactLastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      affiliate.ContactEmail?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesStatus && matchesSearch;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { color: 'bg-yellow-100 text-yellow-800', icon: faCalendar },
      ACTIVE: { color: 'bg-green-100 text-green-800', icon: faCheck },
      SUSPENDED: { color: 'bg-red-100 text-red-800', icon: faTimes },
      TERMINATED: { color: 'bg-gray-100 text-gray-800', icon: faTimes },
    };
    
    const config = statusConfig[status] || statusConfig.PENDING;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <FontAwesomeIcon icon={config.icon} className="mr-1" />
        {status}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getActionConfirmationText = (action) => {
    switch(action) {
      case 'ACTIVE':
        return 'Are you sure you want to activate this affiliate? They will receive a welcome email and their referral codes will be reactivated.';
      case 'SUSPENDED':
        return 'Are you sure you want to suspend this affiliate? Their referral codes will be deactivated until reactivated.';
      case 'TERMINATED':
        return 'Are you sure you want to terminate this affiliate? This action can be reversed later if needed.';
      default:
        return `Are you sure you want to ${action.toLowerCase()} this affiliate?`;
    }
  };

  const getSuccessMessage = (status) => {
    switch(status) {
      case 'ACTIVE':
        return 'Affiliate has been successfully activated! Welcome email sent and referral codes are now active.';
      case 'SUSPENDED':
        return 'Affiliate has been suspended. Their referral codes have been deactivated.';
      case 'TERMINATED':
        return 'Affiliate has been terminated. They have been notified by email.';
      default:
        return 'Affiliate status updated successfully!';
    }
  };

  // Table columns configuration for MainTableV2
  const columns = [
    {
      name: 'Company',
      selector: row => row.CompanyName,
      sortable: true,
      cell: row => (
        <div className="flex items-center">
          <FontAwesomeIcon icon={faBuilding} className="mr-2 text-gray-500" />
          <span className="font-medium">{row.CompanyName}</span>
        </div>
      ),
      grow: 2,
    },
    {
      name: 'Contact',
      selector: row => `${row.ContactFirstName} ${row.ContactLastName}`,
      sortable: true,
      cell: row => (
        <div>
          <div className="font-medium">{row.ContactFirstName} {row.ContactLastName}</div>
          <div className="text-sm text-gray-500">{row.ContactEmail}</div>
        </div>
      ),
      grow: 2,
    },
    {
      name: 'Status',
      selector: row => row.Status,
      sortable: true,
      cell: row => getStatusBadge(row.Status),
      grow: 1,
    },
    {
      name: 'Marketing Method',
      selector: row => row.MarketingMethod,
      sortable: true,
      cell: row => (
        <span className="text-sm capitalize">{row.MarketingMethod?.replace('_', ' ') || 'N/A'}</span>
      ),
      grow: 1.5,
    },
    {
      name: 'Expected Monthly',
      selector: row => row.ExpectedMonthlyReferrals,
      sortable: true,
      cell: row => (
        <span className="text-sm">{row.ExpectedMonthlyReferrals || 'N/A'}</span>
      ),
      grow: 1,
    },
    {
      name: 'Total Referrals',
      selector: row => row.TotalReferrals || 0,
      sortable: true,
      cell: row => (
        <div className="text-center">
          <span className="font-medium text-blue-600">{row.TotalReferrals || 0}</span>
        </div>
      ),
      grow: 1,
    },
    {
      name: 'Commissions Earned',
      selector: row => row.TotalCommissions || 0,
      sortable: true,
      cell: row => (
        <div className="text-right">
          <span className="font-medium text-green-600">{formatCurrency(row.TotalCommissions || 0)}</span>
        </div>
      ),
      grow: 1.5,
    },
    {
      name: 'Created Date',
      selector: row => row.CreatedDate,
      sortable: true,
      cell: row => (
        <span className="text-sm text-gray-600">{formatDate(row.CreatedDate)}</span>
      ),
      grow: 1,
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="flex items-center justify-center space-x-2">
          <button
            onClick={() => {
              setSelectedAffiliate(row);
              setShowDetails(true);
              loadAffiliateReferralCodes(row.AffiliateId);
            }}
            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
            title="View Details"
          >
            <FontAwesomeIcon icon={faEye} />
          </button>
          
          <button
            onClick={() => {
              setSelectedAffiliate(row);
              setEditAffiliateData({
                affiliateId: row.AffiliateId,
                companyName: row.CompanyName || '',
                contactFirstName: row.ContactFirstName || '',
                contactLastName: row.ContactLastName || '',
                contactEmail: row.ContactEmail || '',
                contactPhone: row.ContactPhone || '',
                businessType: row.BusinessType || '',
                taxId: row.TaxId || '',
                businessAddress: row.BusinessAddress || '',
                businessCity: row.BusinessCity || '',
                businessState: row.BusinessState || '',
                businessZip: row.BusinessZip || '',
                marketingMethod: row.MarketingMethod || '',
                expectedMonthlyReferrals: row.ExpectedMonthlyReferrals || '',
                affiliateExperience: row.AffiliateExperience || '',
                notes: row.Notes || '',
                defaultCommissionRate: row.DefaultCommissionRate ? (row.DefaultCommissionRate * 100).toFixed(2) : '5.00',
                paymentTerms: row.PaymentTerms || '',
                minimumPayout: row.MinimumPayout || ''
              });
              setShowEditModal(true);
            }}
            className="text-gray-600 hover:text-gray-900 p-1 rounded hover:bg-gray-50"
            title="Edit Affiliate"
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
          
          {row.Status === 'PENDING' && (
            <>
              <button
                onClick={() => {
                  setPendingAction({ affiliateId: row.AffiliateId, action: 'ACTIVE' });
                  setShowApprovalModal(true);
                }}
                className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                title="Approve"
              >
                <FontAwesomeIcon icon={faCheck} />
              </button>
              <button
                onClick={() => {
                  setPendingAction({ affiliateId: row.AffiliateId, action: 'TERMINATED' });
                  setShowApprovalModal(true);
                }}
                className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                title="Reject"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </>
          )}
          
          {row.Status === 'ACTIVE' && (
            <button
              onClick={() => {
                setPendingAction({ affiliateId: row.AffiliateId, action: 'SUSPENDED' });
                setShowApprovalModal(true);
              }}
              className="text-yellow-600 hover:text-yellow-900 p-1 rounded hover:bg-yellow-50"
              title="Suspend"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}

          {(row.Status?.trim().toUpperCase() === 'SUSPENDED' || row.Status?.trim().toUpperCase() === 'TERMINATED') && (
            <button
              onClick={() => {
                setPendingAction({ affiliateId: row.AffiliateId, action: 'ACTIVE' });
                setShowApprovalModal(true);
              }}
              className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
              title="Reactivate"
            >
              <FontAwesomeIcon icon={faCheck} />
            </button>
          )}
        </div>
      ),
      grow: 1,
      ignoreRowClick: true,
    },
  ];

  if (!currentUser?.isAdmin) {
    return <div className="p-6 text-center">Access denied. Admin privileges required.</div>;
  }

  return (
    <>
      <MobileHeader theme={theme} />
      <div className="mobile-container">
        <div className="mb-8">
          <div className="flex items-center mb-6">
            <FontAwesomeIcon icon={faHandshake} className="text-3xl mr-3" style={{ color: primaryColor }} />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Affiliate Management</h1>
          </div>
          
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FontAwesomeIcon icon={faHandshake} className="text-2xl text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Affiliates</p>
                  <p className="text-2xl font-semibold text-gray-900">{affiliates.length}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FontAwesomeIcon icon={faCalendar} className="text-2xl text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Pending Approval</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {affiliates.filter(a => a.Status === 'PENDING').length}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FontAwesomeIcon icon={faCheck} className="text-2xl text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Active Affiliates</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {affiliates.filter(a => a.Status === 'ACTIVE').length}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FontAwesomeIcon icon={faDollarSign} className="text-2xl text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Commissions</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(affiliates.reduce((sum, a) => sum + a.TotalCommissions, 0))}
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Action Buttons */}
        <div className="flex justify-end mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <button 
              onClick={() => {
                setShowAddModal(true);
                loadAvailableUsers();
              }}
              className="text-white font-semibold py-2 px-4 rounded-lg flex items-center justify-center hover:opacity-90 transition-opacity"
              style={{ backgroundColor: primaryColor }}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Add Affiliate
            </button>
            <button 
              className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg flex items-center justify-center transition-colors"
            >
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              Export Data
            </button>
          </div>
        </div>

        {/* MainTableV2 with sortable columns */}
        <MainTableV2
          columns={columns}
          data={filteredAffiliates}
          primaryColor={primaryColor}  
          loading={loading}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filterConfig={[
            {
              key: 'status',
              label: 'Status',
              options: [
                { label: 'All Status', value: 'ALL' },
                { label: 'Pending', value: 'PENDING' },
                { label: 'Active', value: 'ACTIVE' },
                { label: 'Suspended', value: 'SUSPENDED' },
                { label: 'Terminated', value: 'TERMINATED' },
              ],
              value: filterStatus,
              onChange: setFilterStatus,
            },
          ]}
        />

        {/* Success/Error Message Display */}
        {submitStatus && (
          <div className={`fixed top-4 right-4 z-50 max-w-sm w-full mx-4 p-4 rounded-lg shadow-lg transform transition-all duration-300 ${
            submitStatus === 'success' ? 'bg-green-50 border border-green-200 text-green-800' :
            submitStatus === 'error' ? 'bg-red-50 border border-red-200 text-red-800' :
            'bg-blue-50 border border-blue-200 text-blue-800'
          }`}>
            <div className="flex items-center">
              {submitStatus === 'loading' && (
                <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
              )}
              {submitStatus === 'success' && (
                <FontAwesomeIcon icon={faCheck} className="text-green-500 mr-3" />
              )}
              {submitStatus === 'error' && (
                <FontAwesomeIcon icon={faTimes} className="text-red-500 mr-3" />
              )}
              <span className="text-sm font-medium">{submitMessage}</span>
            </div>
          </div>
        )}

        {/* Approval Modal */}
        {showApprovalModal && pendingAction && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Confirm Action
              </h3>
              <p className="text-gray-600 mb-6">
                {getActionConfirmationText(pendingAction.action)}
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowApprovalModal(false);
                    setPendingAction(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleStatusChange(pendingAction.affiliateId, pendingAction.action)}
                  className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center"
                  style={{ backgroundColor: primaryColor }}
                  disabled={submitStatus === 'loading'}
                >
                  {submitStatus === 'loading' ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Processing...
                    </>
                  ) : (
                    'Confirm'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Details Modal */}
        {showDetails && selectedAffiliate && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full h-[90vh] sm:h-[85vh] flex flex-col">
              {/* Fixed Header */}
              <div className="flex justify-between items-center p-6 border-b border-gray-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <FontAwesomeIcon icon={faBuilding} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-medium text-gray-900">
                      {selectedAffiliate.CompanyName}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {selectedAffiliate.ContactFirstName} {selectedAffiliate.ContactLastName}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              
              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-3">Company Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Company Name</label>
                      <p className="text-sm text-gray-900">{selectedAffiliate.CompanyName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <div className="mt-1">{getStatusBadge(selectedAffiliate.Status)}</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-3">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Name</label>
                      <p className="text-sm text-gray-900">
                        {selectedAffiliate.ContactFirstName} {selectedAffiliate.ContactLastName}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <p className="text-sm text-gray-900">{selectedAffiliate.ContactEmail}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Phone</label>
                      <p className="text-sm text-gray-900">{selectedAffiliate.ContactPhone}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Address</label>
                      <p className="text-sm text-gray-900">
                        {selectedAffiliate.BusinessAddress}<br />
                        {selectedAffiliate.BusinessCity}, {selectedAffiliate.BusinessState} {selectedAffiliate.BusinessZip}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-3">Marketing Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Marketing Method</label>
                      <p className="text-sm text-gray-900 capitalize">
                        {selectedAffiliate.MarketingMethod?.replace('_', ' ')}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Expected Monthly Referrals</label>
                      <p className="text-sm text-gray-900">{selectedAffiliate.ExpectedMonthlyReferrals}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Experience Level</label>
                      <p className="text-sm text-gray-900 capitalize">{selectedAffiliate.AffiliateExperience}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Commission Rate</label>
                      <p className="text-sm text-gray-900">
                        {(selectedAffiliate.DefaultCommissionRate * 100).toFixed(2)}%
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-3">Performance</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Total Referrals</label>
                      <p className="text-sm text-gray-900">{selectedAffiliate.TotalReferrals}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Total Commissions</label>
                      <p className="text-sm text-gray-900">{formatCurrency(selectedAffiliate.TotalCommissions)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Paid Commissions</label>
                      <p className="text-sm text-gray-900">{formatCurrency(selectedAffiliate.PaidCommissions)}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-lg font-medium text-gray-900">Referral Codes & Salesperson Data</h4>
                    <button 
                      onClick={() => setShowAddCodeModal(true)}
                      className="text-white text-sm px-3 py-1 rounded-md flex items-center hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-1" />
                      Add Code
                    </button>
                  </div>
                  {affiliateReferralCodes.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Referral Code
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Salesperson
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Campaign
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Status
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Commission Rate
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Uses/Commissions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {affiliateReferralCodes.map((code, index) => (
                            <tr key={code.AffiliateCodeId || index}>
                              <td className="px-4 py-2 text-sm font-medium text-gray-900">
                                {code.ReferralCode}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                <div>{code.SalespersonName}</div>
                                <div className="text-xs text-gray-500">{code.SalespersonEmail}</div>
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                <div>{code.CampaignName}</div>
                                <div className="text-xs text-gray-500 capitalize">{code.CampaignType?.replace('_', ' ')}</div>
                              </td>
                              <td className="px-4 py-2 text-sm">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  code.IsActive 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {code.IsActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {code.CommissionRate ? 
                                  `${(code.CommissionRate * 100).toFixed(2)}%` : 
                                  `${(selectedAffiliate.DefaultCommissionRate * 100).toFixed(2)}%`
                                }
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                <div>{code.TotalUses || 0} uses</div>
                                <div className="text-xs text-gray-500">
                                  {formatCurrency(code.TotalCommissions || 0)} earned
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 py-4">
                      No referral codes found for this affiliate.
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-3">Dates</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Applied Date</label>
                      <p className="text-sm text-gray-900">{formatDate(selectedAffiliate.CreatedDate)}</p>
                    </div>
                    {selectedAffiliate.ApprovedDate && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Approved Date</label>
                        <p className="text-sm text-gray-900">{formatDate(selectedAffiliate.ApprovedDate)}</p>
                      </div>
                    )}
                  </div>
                </div>
                </div>
              </div>
              
              {/* Fixed Footer */}
              <div className="border-t border-gray-200 p-6 bg-gray-50 rounded-b-lg">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-4">
                    <div className="text-sm text-gray-600">
                      Status: {getStatusBadge(selectedAffiliate.Status)}
                    </div>
                    <div className="text-sm text-gray-600">
                      Created: {formatDate(selectedAffiliate.CreatedDate)}
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    {selectedAffiliate.Status === 'PENDING' && (
                      <>
                        <button
                          onClick={() => {
                            setPendingAction({ affiliateId: selectedAffiliate.AffiliateId, action: 'TERMINATED' });
                            setShowApprovalModal(true);
                            setShowDetails(false);
                          }}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
                        >
                          <FontAwesomeIcon icon={faTimes} className="mr-2" />
                          Reject
                        </button>
                        <button
                          onClick={() => {
                            setPendingAction({ affiliateId: selectedAffiliate.AffiliateId, action: 'ACTIVE' });
                            setShowApprovalModal(true);
                            setShowDetails(false);
                          }}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                        >
                          <FontAwesomeIcon icon={faCheck} className="mr-2" />
                          Approve
                        </button>
                      </>
                    )}
                    {selectedAffiliate.Status === 'ACTIVE' && (
                      <button
                        onClick={() => {
                          setPendingAction({ affiliateId: selectedAffiliate.AffiliateId, action: 'SUSPENDED' });
                          setShowApprovalModal(true);
                          setShowDetails(false);
                        }}
                        className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center"
                      >
                        <FontAwesomeIcon icon={faTimes} className="mr-2" />
                        Suspend
                      </button>
                    )}
                    {(selectedAffiliate.Status?.trim().toUpperCase() === 'SUSPENDED' || selectedAffiliate.Status?.trim().toUpperCase() === 'TERMINATED') && (
                      <button
                        onClick={() => {
                          setPendingAction({ affiliateId: selectedAffiliate.AffiliateId, action: 'ACTIVE' });
                          setShowApprovalModal(true);
                          setShowDetails(false);
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                      >
                        <FontAwesomeIcon icon={faCheck} className="mr-2" />
                        Reactivate
                      </button>
                    )}
                    <button
                      onClick={() => setShowDetails(false)}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Affiliate Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full h-[95vh] sm:h-[90vh] lg:h-[85vh] flex flex-col">
              {/* Fixed Header */}
              <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    <FontAwesomeIcon icon={faPlus} className="text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-medium text-gray-900">
                      Add New Affiliate Partner
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500">Create affiliate account with immediate approval</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              
              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              
              <form id="add-affiliate-form" onSubmit={handleAddAffiliate} className="space-y-6">
                {/* Contact Information */}
                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={newAffiliateData.firstName}
                        onChange={handleInputChange}
                        required
                        className="w-full p-2 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': primaryColor }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={newAffiliateData.lastName}
                        onChange={handleInputChange}
                        required
                        className="w-full p-2 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': primaryColor }}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={newAffiliateData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full p-2 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': primaryColor }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={newAffiliateData.phone}
                        onChange={handleInputChange}
                        className="w-full p-2 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': primaryColor }}
                      />
                    </div>
                  </div>
                </div>

                {/* Business Information */}
                <div className="space-y-4 border-t border-gray-200 pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Business Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={newAffiliateData.company}
                        onChange={handleInputChange}
                        required
                        className="w-full p-2 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': primaryColor }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Business Type
                      </label>
                      <select
                        name="businessType"
                        value={newAffiliateData.businessType}
                        onChange={handleInputChange}
                        className="w-full p-2 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': primaryColor }}
                      >
                        <option value="">Select business type</option>
                        <option value="Transportation Company">Transportation Company</option>
                        <option value="Fleet Manager">Fleet Manager</option>
                        <option value="Owner Operator">Owner Operator</option>
                        <option value="Fuel Consultant">Fuel Consultant</option>
                        <option value="Industry Blogger/Influencer">Industry Blogger/Influencer</option>
                        <option value="Equipment Dealer">Equipment Dealer</option>
                        <option value="Insurance Agent">Insurance Agent</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tax ID
                    </label>
                    <input
                      type="text"
                      name="taxId"
                      value={newAffiliateData.taxId}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Business Address */}
                <div className="space-y-4 border-t border-gray-200 pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Business Address</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Street Address
                    </label>
                    <input
                      type="text"
                      name="businessAddress"
                      value={newAffiliateData.businessAddress}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <input
                        type="text"
                        name="businessCity"
                        value={newAffiliateData.businessCity}
                        onChange={handleInputChange}
                        className="w-full p-2 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': primaryColor }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                      <input
                        type="text"
                        name="businessState"
                        value={newAffiliateData.businessState}
                        onChange={handleInputChange}
                        className="w-full p-2 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': primaryColor }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ZIP Code
                      </label>
                      <input
                        type="text"
                        name="businessZip"
                        value={newAffiliateData.businessZip}
                        onChange={handleInputChange}
                        className="w-full p-2 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': primaryColor }}
                      />
                    </div>
                  </div>
                </div>

                {/* Marketing Information */}
                <div className="space-y-4 border-t border-gray-200 pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Marketing Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Marketing Method *
                      </label>
                      <select
                        name="referralMethod"
                        value={newAffiliateData.referralMethod}
                        onChange={handleInputChange}
                        required
                        className="w-full p-2 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': primaryColor }}
                      >
                        <option value="">Select method</option>
                        <option value="social_media">Social Media</option>
                        <option value="website_blog">Website/Blog</option>
                        <option value="email_marketing">Email Marketing</option>
                        <option value="direct_referrals">Direct Referrals</option>
                        <option value="industry_events">Industry Events</option>
                        <option value="youtube_content">YouTube/Video Content</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Expected Monthly Referrals *
                      </label>
                      <select
                        name="expectedReferrals"
                        value={newAffiliateData.expectedReferrals}
                        onChange={handleInputChange}
                        required
                        className="w-full p-2 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': primaryColor }}
                      >
                        <option value="">Select range</option>
                        <option value="1-5">1-5 referrals</option>
                        <option value="6-15">6-15 referrals</option>
                        <option value="16-30">16-30 referrals</option>
                        <option value="31-50">31-50 referrals</option>
                        <option value="50+">50+ referrals</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Experience Level
                    </label>
                    <select
                      name="experience"
                      value={newAffiliateData.experience}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="">Select experience</option>
                      <option value="new">New to affiliate marketing</option>
                      <option value="some">Some experience (1-2 years)</option>
                      <option value="experienced">Experienced (3+ years)</option>
                      <option value="expert">Expert (5+ years)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Comments
                    </label>
                    <textarea
                      name="comments"
                      value={newAffiliateData.comments}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Additional notes about this affiliate..."
                    />
                  </div>
                </div>

              </form>
              </div>
              
              {/* Fixed Footer */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 sm:p-6 border-t border-gray-200 bg-gray-50 gap-3 sm:gap-0">
                <div className="text-xs sm:text-sm text-gray-600 order-2 sm:order-1">
                  <span className="flex items-start sm:items-center">
                    <FontAwesomeIcon icon={faInfoCircle} className="mr-2 mt-0.5 sm:mt-0" style={{ color: primaryColor }} />
                    <span className="leading-tight">Creating affiliate will automatically send welcome email with account setup link</span>
                  </span>
                </div>
                <div className="flex space-x-3 order-1 sm:order-2 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 sm:flex-none px-4 py-2 text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="add-affiliate-form"
                    className="flex-1 sm:flex-none px-4 sm:px-6 py-2 text-white rounded-lg font-medium flex items-center justify-center hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: primaryColor }}
                    disabled={submitStatus === 'loading'}
                  >
                    {submitStatus === 'loading' ? (
                      <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                    ) : (
                      <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    )}
                    {submitStatus === 'loading' ? 'Creating...' : 'Create Affiliate Partner'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Affiliate Modal */}
        {showEditModal && selectedAffiliate && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full h-[95vh] sm:h-[90vh] lg:h-[85vh] flex flex-col">
              {/* Fixed Header */}
              <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                    <FontAwesomeIcon icon={faEdit} className="text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-medium text-gray-900">
                      Edit Affiliate Partner
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500">Update affiliate information</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              
              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                <form id="edit-affiliate-form" onSubmit={handleEditAffiliate} className="space-y-6">
                  {/* Contact Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          First Name *
                        </label>
                        <input
                          type="text"
                          name="contactFirstName"
                          value={editAffiliateData.contactFirstName}
                          onChange={handleEditInputChange}
                          required
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          name="contactLastName"
                          value={editAffiliateData.contactLastName}
                          onChange={handleEditInputChange}
                          required
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="contactEmail"
                          value={editAffiliateData.contactEmail}
                          onChange={handleEditInputChange}
                          required
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          name="contactPhone"
                          value={editAffiliateData.contactPhone}
                          onChange={handleEditInputChange}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Business Information */}
                  <div className="space-y-4 border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Business Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Company Name *
                        </label>
                        <input
                          type="text"
                          name="companyName"
                          value={editAffiliateData.companyName}
                          onChange={handleEditInputChange}
                          required
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Business Type
                        </label>
                        <select
                          name="businessType"
                          value={editAffiliateData.businessType}
                          onChange={handleEditInputChange}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        >
                          <option value="">Select business type</option>
                          <option value="Transportation Company">Transportation Company</option>
                          <option value="Fleet Manager">Fleet Manager</option>
                          <option value="Owner Operator">Owner Operator</option>
                          <option value="Fuel Consultant">Fuel Consultant</option>
                          <option value="Industry Blogger/Influencer">Industry Blogger/Influencer</option>
                          <option value="Equipment Dealer">Equipment Dealer</option>
                          <option value="Insurance Agent">Insurance Agent</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tax ID
                      </label>
                      <input
                        type="text"
                        name="taxId"
                        value={editAffiliateData.taxId}
                        onChange={handleEditInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Business Address */}
                  <div className="space-y-4 border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Business Address</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Street Address
                      </label>
                      <input
                        type="text"
                        name="businessAddress"
                        value={editAffiliateData.businessAddress}
                        onChange={handleEditInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          City
                        </label>
                        <input
                          type="text"
                          name="businessCity"
                          value={editAffiliateData.businessCity}
                          onChange={handleEditInputChange}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          State
                        </label>
                        <input
                          type="text"
                          name="businessState"
                          value={editAffiliateData.businessState}
                          onChange={handleEditInputChange}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ZIP Code
                        </label>
                        <input
                          type="text"
                          name="businessZip"
                          value={editAffiliateData.businessZip}
                          onChange={handleEditInputChange}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Marketing Information */}
                  <div className="space-y-4 border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Marketing Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Marketing Method
                        </label>
                        <select
                          name="marketingMethod"
                          value={editAffiliateData.marketingMethod}
                          onChange={handleEditInputChange}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        >
                          <option value="">Select method</option>
                          <option value="social_media">Social Media</option>
                          <option value="website_blog">Website/Blog</option>
                          <option value="email_marketing">Email Marketing</option>
                          <option value="direct_referrals">Direct Referrals</option>
                          <option value="industry_events">Industry Events</option>
                          <option value="youtube_content">YouTube/Video Content</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Expected Monthly Referrals
                        </label>
                        <select
                          name="expectedMonthlyReferrals"
                          value={editAffiliateData.expectedMonthlyReferrals}
                          onChange={handleEditInputChange}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        >
                          <option value="">Select range</option>
                          <option value="1-5">1-5 referrals</option>
                          <option value="6-15">6-15 referrals</option>
                          <option value="16-30">16-30 referrals</option>
                          <option value="31-50">31-50 referrals</option>
                          <option value="50+">50+ referrals</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Experience Level
                      </label>
                      <select
                        name="affiliateExperience"
                        value={editAffiliateData.affiliateExperience}
                        onChange={handleEditInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        <option value="">Select experience</option>
                        <option value="new">New to affiliate marketing</option>
                        <option value="some">Some experience (1-2 years)</option>
                        <option value="experienced">Experienced (3+ years)</option>
                        <option value="expert">Expert (5+ years)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes
                      </label>
                      <textarea
                        name="notes"
                        value={editAffiliateData.notes}
                        onChange={handleEditInputChange}
                        rows={3}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Additional notes about this affiliate..."
                      />
                    </div>
                  </div>

                  {/* Commission Settings */}
                  <div className="space-y-4 border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Commission Settings</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Default Commission Rate (%) *
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            name="defaultCommissionRate"
                            value={editAffiliateData.defaultCommissionRate}
                            onChange={handleEditInputChange}
                            step="0.01"
                            min="0"
                            max="100"
                            required
                            className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent pr-8"
                            placeholder="5.00"
                          />
                          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Commission rate as percentage (e.g., 5.00 for 5%)</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Minimum Payout ($)
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                          <input
                            type="number"
                            name="minimumPayout"
                            value={editAffiliateData.minimumPayout}
                            onChange={handleEditInputChange}
                            step="0.01"
                            min="0"
                            className="w-full p-3 pl-8 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="50.00"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Minimum amount before payout (leave empty for no minimum)</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Payment Terms
                        </label>
                        <select
                          name="paymentTerms"
                          value={editAffiliateData.paymentTerms}
                          onChange={handleEditInputChange}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        >
                          <option value="">Select terms</option>
                          <option value="NET15">Net 15 Days</option>
                          <option value="NET30">Net 30 Days</option>
                          <option value="NET45">Net 45 Days</option>
                          <option value="NET60">Net 60 Days</option>
                          <option value="MONTHLY">Monthly</option>
                          <option value="QUARTERLY">Quarterly</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Payment schedule for commission payouts</p>
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 mt-0.5 mr-2" />
                        <div className="text-sm text-blue-700">
                          <p className="font-medium mb-1">Commission Information:</p>
                          <ul className="list-disc list-inside space-y-1 text-xs">
                            <li>Default commission rate applies to all referral codes unless overridden</li>
                            <li>Commission is calculated as a percentage of profit margin on fuel purchased by referred customers</li>
                            <li>Individual referral codes can have custom rates that override this default</li>
                            <li>Changes to commission rates affect future transactions only</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              
              {/* Fixed Footer */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 sm:p-6 border-t border-gray-200 bg-gray-50 gap-3 sm:gap-0">
                <div className="text-xs sm:text-sm text-gray-600 order-2 sm:order-1">
                  <span className="flex items-start sm:items-center">
                    <FontAwesomeIcon icon={faInfoCircle} className="mr-2 mt-0.5 sm:mt-0" style={{ color: primaryColor }} />
                    <span className="leading-tight">Update affiliate information and save changes</span>
                  </span>
                </div>
                <div className="flex space-x-3 order-1 sm:order-2 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 sm:flex-none px-4 py-2 text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="edit-affiliate-form"
                    className="flex-1 sm:flex-none px-4 sm:px-6 py-2 text-white rounded-lg font-medium flex items-center justify-center hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: primaryColor }}
                    disabled={submitStatus === 'loading'}
                  >
                    {submitStatus === 'loading' ? (
                      <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                    ) : (
                      <FontAwesomeIcon icon={faEdit} className="mr-2" />
                    )}
                    {submitStatus === 'loading' ? 'Updating...' : 'Update Affiliate'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Referral Code Modal */}
        {showAddCodeModal && selectedAffiliate && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-lg p-4 sm:p-6 max-w-lg w-full max-h-[95vh] sm:max-h-screen overflow-y-auto">
              <div className="flex justify-between items-start mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-medium text-gray-900 pr-4">
                  Add Referral Code for {selectedAffiliate.CompanyName}
                </h3>
                <button
                  onClick={() => setShowAddCodeModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              
              <form onSubmit={handleAddReferralCode} className="space-y-4">
                {/* Salesperson Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Salesperson Name *
                    </label>
                    <input
                      type="text"
                      name="salespersonName"
                      value={newCodeData.salespersonName}
                      onChange={handleCodeInputChange}
                      required
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                      style={{ '--tw-ring-color': primaryColor }}
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Salesperson Email *
                    </label>
                    <input
                      type="email"
                      name="salespersonEmail"
                      value={newCodeData.salespersonEmail}
                      onChange={handleCodeInputChange}
                      required
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                      style={{ '--tw-ring-color': primaryColor }}
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                {/* Campaign Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Campaign Name *
                    </label>
                    <input
                      type="text"
                      name="campaignName"
                      value={newCodeData.campaignName}
                      onChange={handleCodeInputChange}
                      required
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                      style={{ '--tw-ring-color': primaryColor }}
                      placeholder="Q1 2025 Campaign"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Campaign Type *
                    </label>
                    <select
                      name="campaignType"
                      value={newCodeData.campaignType}
                      onChange={handleCodeInputChange}
                      required
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                      style={{ '--tw-ring-color': primaryColor }}
                    >
                      <option value="direct_referrals">Direct Referrals</option>
                      <option value="social_media">Social Media</option>
                      <option value="email_marketing">Email Marketing</option>
                      <option value="website_blog">Website/Blog</option>
                      <option value="industry_events">Industry Events</option>
                      <option value="youtube_content">YouTube/Video</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                {/* Code Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Commission Rate (%)
                    </label>
                    <input
                      type="number"
                      name="commissionRate"
                      value={newCodeData.commissionRate}
                      onChange={handleCodeInputChange}
                      step="0.01"
                      min="0"
                      max="100"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                      style={{ '--tw-ring-color': primaryColor }}
                      placeholder="5.00 (leave empty for default)"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty to use affiliate's default rate</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Uses
                    </label>
                    <input
                      type="number"
                      name="maxUses"
                      value={newCodeData.maxUses}
                      onChange={handleCodeInputChange}
                      min="1"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                      style={{ '--tw-ring-color': primaryColor }}
                      placeholder="Leave empty for unlimited"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expiration Date
                  </label>
                  <input
                    type="date"
                    name="expirationDate"
                    value={newCodeData.expirationDate}
                    onChange={handleCodeInputChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty for no expiration</p>
                </div>

                {/* Active Status */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={newCodeData.isActive}
                    onChange={handleCodeInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-700">
                    Activate code immediately
                  </label>
                </div>

                {/* Submit Buttons */}
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddCodeModal(false)}
                    className="w-full sm:w-auto px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg order-2 sm:order-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity order-1 sm:order-2 flex items-center justify-center"
                    style={{ backgroundColor: primaryColor }}
                    disabled={submitStatus === 'loading'}
                  >
                    {submitStatus === 'loading' ? (
                      <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                    ) : null}
                    {submitStatus === 'loading' ? 'Creating...' : 'Create Referral Code'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AffiliateManagement;